import React from 'react'
import { Radio, Tooltip } from '@chakra-ui/react'
import { Box, Highlight, Text, Flex } from '@chakra-ui/react'

import {
    AppointmentFieldsFragment,
    ChatClinicFieldsFragment,
    ClientPreviewFieldsFragment,
    ClinicianFieldsFragment,
    MessageTemplateFieldsFragment,
} from '~graphql/generated/graphql'
import { PatientIdAndName } from '../types'
import SharedLabel from '~components/shared/form/shared-label'
import OutlineBox from '~components/shared/wrappers/outline-box'
import {
    getTemplateMapper,
    hasAppointmentSelectionTemplateVariables,
    replaceTemplateVariables,
} from '~utils/replace-template-variables'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { Tag } from '@chakra-ui/react'
import { getCopyTemplateMissingInfoHelp } from '../missing-info-help'

interface Props {
    clinic: ChatClinicFieldsFragment
    template: MessageTemplateFieldsFragment
    client: ClientPreviewFieldsFragment
    patient: PatientIdAndName | undefined
    appointment?: Pick<
        AppointmentFieldsFragment,
        'id' | 'start_time' | 'drop_off_time' | 'service'
    > & {
        clinician?:
            | Pick<ClinicianFieldsFragment, 'name' | 'display_name'>
            | null
            | undefined
    }
    clientPreviousDoctor?: string | undefined
    patientPreviousDoctor?: string | undefined
    googleCalendarEvent?: string | undefined
    isSelected: boolean
    onClick?: () => void
    query: string
    /** Render as a static preview: no pointer cursor or hover affordance. */
    readOnly?: boolean
}

export default function MessageTemplateOption({
    clinic,
    client,
    template,
    patient,
    appointment,
    clientPreviousDoctor,
    patientPreviousDoctor,
    googleCalendarEvent,
    isSelected,
    onClick,
    query,
    readOnly = false,
}: Props) {
    const { name, body } = template
    const { value: modifiedBody } = replaceTemplateVariables(
        body.slice(0, 501),
        getTemplateMapper({
            client: client,
            clinic,
            patient,
            appointment,
            clientPreviousDoctor,
            patientPreviousDoctor,
            googleCalendarEvent,
        }),
    )

    const hasAppointmentFields = hasAppointmentSelectionTemplateVariables(body)
    const missingInfoHelp = getCopyTemplateMissingInfoHelp({
        missingPatient: body.includes('patient_name') && !patient,
        missingAppointment: hasAppointmentFields && !appointment,
    })

    return (
        <OutlineBox
            w="full"
            shadow="none"
            cursor={readOnly ? 'default' : 'pointer'}
            onClick={readOnly ? undefined : onClick}
            _hover={
                readOnly ? {} : { bg: isSelected ? 'brand.100' : 'gray.50' }
            }
            bg={isSelected ? 'brand.50' : 'white'}
            borderColor={isSelected ? 'brand.500' : 'blackAlpha.200'}
        >
            <Flex
                w="full"
                justifyContent="space-between"
                alignItems={{ base: 'flex-start', md: 'center' }}
                gap={3}
            >
                <Box minW={0} flex={1}>
                    <SharedStack row spacing={2}>
                        <SharedLabel mb={1} fontSize="sm">
                            <Highlight
                                query={query}
                                styles={{ bg: 'brand.100' }}
                            >
                                {name}
                            </Highlight>
                        </SharedLabel>
                    </SharedStack>
                    <Tooltip
                        label={missingInfoHelp}
                        fontSize="xs"
                        placement="top"
                        hasArrow
                        isDisabled={!missingInfoHelp}
                    >
                        <Text
                            whiteSpace="normal"
                            overflowWrap="break-word"
                            noOfLines={3}
                            fontSize="xs"
                        >
                            {modifiedBody}
                        </Text>
                    </Tooltip>
                    {hasAppointmentFields && (
                        <Tag size="sm" colorScheme="yellow" mt={1}>
                            Has Appointment Fields
                        </Tag>
                    )}
                </Box>
                <Radio
                    isChecked={isSelected}
                    colorScheme="brand"
                    mt={{ base: 1, md: 0 }}
                    flexShrink={0}
                />
            </Flex>
        </OutlineBox>
    )
}
